import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { agrogestao } from "@/api/agrogestaoClient";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Tractor, Eye, EyeOff, Mail, Lock, ShieldCheck } from "lucide-react";

export default function Login({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [currentMode, setCurrentMode] = useState(mode || "signin");
  const [adminLogin, setAdminLogin] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get("action");
    if (action === "reset") {
      setCurrentMode("reset");
    } else if (action === "signup") {
      setCurrentMode("signup");
    }
    const isAdmin = params.get("admin");
    if (isAdmin === '1' || isAdmin === 'true') {
      setAdminLogin(true);
      setCurrentMode('signin');
    }
    const savedEmail = localStorage.getItem("remember_email");
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const emailOk = /.+@.+\..+/.test(email);
      if (!emailOk) throw new Error("E-mail inválido");
      if ((currentMode === 'signin' || currentMode === 'signup') && (!password || password.length < 6)) {
        throw new Error("Senha deve ter pelo menos 6 caracteres");
      }
      if (currentMode === "signin") {
        await agrogestao.auth.signInWithPassword({ email, password });
        if (remember) localStorage.setItem("remember_email", email);
        try {
          const { data } = await agrogestao.client.from('users').select('email, role').eq('email', email).maybeSingle();
          const admin = (data?.role === 'admin') || /softwarehouse/i.test(email) || /@agrogestao\.com\.br$/i.test(email) || /@aguiatecnologia\.com\.br$/i.test(email);
          if (admin) {
            navigate('/Admin', { replace: true });
          } else {
            navigate('/Dashboard', { replace: true });
          }
        } catch (err) {
          navigate('/Dashboard', { replace: true });
        }
      } else if (currentMode === "signup") {
        const res = await agrogestao.auth.signUpWithPassword({ email, password, data: { full_name: name } });
        try {
          await agrogestao.client.from('users').upsert({ email, full_name: name }, { onConflict: 'email' });
        } catch (e) { void 0; }
        setInfo("Verifique seu e-mail para confirmar o cadastro");
      } else if (currentMode === "reset") {
        if (!password || password !== password2) {
          throw new Error("Senhas não conferem");
        }
        await agrogestao.auth.updatePassword(password);
        navigate("/Dashboard", { replace: true });
      } else if (currentMode === "magic") {
        const redirectTo = window.location.origin;
        await agrogestao.auth.signInWithOtp({ email, redirectTo });
        setInfo("Link enviado. Verifique seu e-mail");
      }
    } catch (err) {
      setError("Erro: " + (err?.message || "tente novamente"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const redirectTo = window.location.origin + "/?action=reset";
      await agrogestao.auth.resetPasswordForEmail(email, redirectTo);
      setInfo("E-mail de recuperação enviado");
    } catch (err) {
      setError("Erro: " + (err?.message || "tente novamente"));
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 flex items-center justify-center p-6">
      <style>{`
        :root {
          --verde-escuro: #1B4332;
          --verde-medio: #2D6A4F;
          --verde-claro: #40916C;
          --dourado: #D4A373;
        }
      `}</style>

      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="w-10 h-10 bg-[var(--dourado)] rounded-xl flex items-center justify-center">
            <Tractor className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-2xl text-[var(--verde-escuro)]">AgroGestão</h1>
            <p className="text-sm text-stone-600">Gestão rural com praticidade</p>
          </div>
        </div>

        <Card className="shadow-2xl">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-6 md:p-8">
                <div className="flex gap-2 mb-4">
                  <button className={`px-3 py-1 rounded ${currentMode === 'signin' ? 'bg-stone-200' : 'bg-stone-100'}`} onClick={() => setCurrentMode('signin')}>Entrar</button>
                  <button className={`px-3 py-1 rounded ${currentMode === 'signup' ? 'bg-stone-200' : 'bg-stone-100'}`} onClick={() => setCurrentMode('signup')}>Criar conta</button>
                  <button className={`px-3 py-1 rounded ${currentMode === 'magic' ? 'bg-stone-200' : 'bg-stone-100'}`} onClick={() => setCurrentMode('magic')}>Link mágico</button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {currentMode !== 'reset' && (
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seuemail@exemplo.com" className="pl-9" required />
                      </div>
                    </div>
                  )}

                  {(currentMode === 'signin' || currentMode === 'signup') && (
                    <div className="space-y-2">
                      <Label htmlFor="password">Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 pr-9" required />
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="w-4 h-4 text-stone-500" /> : <Eye className="w-4 h-4 text-stone-500" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {currentMode === 'signup' && (
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome completo</Label>
                      <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" required />
                    </div>
                  )}

                  {currentMode === 'reset' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="newpassword">Nova senha</Label>
                        <Input id="newpassword" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newpassword2">Confirmar senha</Label>
                        <Input id="newpassword2" type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} placeholder="••••••••" required />
                      </div>
                    </>
                  )}

                  {error && <div className="text-red-600 text-sm">{error}</div>}
                  {info && <div className="text-green-700 text-sm">{info}</div>}

                  {currentMode === 'signin' && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-stone-600">
                    <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    Lembrar e-mail
                  </label>
                  <button type="button" className="text-sm text-[var(--verde-claro)]" onClick={handleForgotPassword} disabled={loading}>Esqueci minha senha</button>
                </div>
              )}

                  <Button type="submit" className="w-full bg-[var(--verde-medio)] hover:bg-[var(--verde-claro)]" disabled={loading}>
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Confirmando
                      </span>
                    ) : (
                      currentMode === 'signin' ? 'Entrar' : currentMode === 'signup' ? 'Criar conta' : currentMode === 'reset' ? 'Atualizar senha' : 'Enviar link'
                    )}
                  </Button>

                  
                </form>
              </div>
              <div className="hidden md:block p-8 bg-gradient-to-br from-[#1B4332] to-[#2D6A4F] text-white rounded-r-xl">
                <div className="space-y-4">
                  <div className="text-2xl font-semibold">Bem-vindo</div>
                  <div className="text-sm text-white/80">Acesse sua conta para continuar gerenciando sua produção, finanças e estoques.</div>
                  {adminLogin && <div className="mt-2 text-xs text-amber-200">Acesso restrito à SoftwareHouse</div>}
                  <div className="mt-6 grid grid-cols-1 gap-3">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-5 h-5 text-[#D4A373]" />
                      <span className="text-sm">Segurança e praticidade</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-[#D4A373]" />
                      <span className="text-sm">Autenticação moderna</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
